import { useEffect, useRef, useState } from 'react'
import type { SummaryFilterValues, SummaryOptions } from '../../../shared/api/analytics.api'

interface Option {
  value: string
  label: string
}

function MultiSelect({
  label,
  options,
  selected,
  onChange,
  searchable = false,
}: {
  label: string
  options: Option[]
  selected: string[]
  onChange: (v: string[]) => void
  searchable?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v])

  const filtered =
    searchable && q
      ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()))
      : options

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
          selected.length
            ? 'border-brand-300 bg-brand-50 text-brand-700'
            : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
        }`}
      >
        {label}
        {selected.length > 0 && <span className="ml-1 font-semibold">· {selected.length}</span>}
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-60 max-h-72 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg p-2">
          {searchable && (
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Поиск…"
              className="w-full mb-2 border border-gray-200 rounded-md px-2 py-1 text-sm"
            />
          )}
          {selected.length > 0 && (
            <button
              onClick={() => onChange([])}
              className="w-full text-left text-xs text-gray-500 hover:text-gray-800 px-1 py-1"
            >
              Очистить ({selected.length})
            </button>
          )}
          {filtered.length === 0 && (
            <div className="px-1 py-2 text-xs text-gray-400">Нет опций</div>
          )}
          {filtered.map((o) => (
            <label
              key={o.value}
              className="flex items-center gap-2 px-1 py-1 text-sm text-gray-700 hover:bg-gray-50 rounded cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selected.includes(o.value)}
                onChange={() => toggle(o.value)}
              />
              <span className="truncate" title={o.label}>
                {o.label}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

export function SummaryFilters({
  options,
  value,
  onChange,
}: {
  options: SummaryOptions | undefined
  value: SummaryFilterValues
  onChange: (v: SummaryFilterValues) => void
}) {
  const patch = (p: Partial<SummaryFilterValues>) => onChange({ ...value, ...p })
  const active =
    (value.managerIds?.length ?? 0) +
      (value.segments?.length ?? 0) +
      (value.cities?.length ?? 0) +
      (value.brands?.length ?? 0) >
      0 || !!value.type

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <MultiSelect
        label="Менеджеры"
        options={(options?.managers ?? []).map((m) => ({ value: m.id, label: m.name }))}
        selected={value.managerIds ?? []}
        onChange={(v) => patch({ managerIds: v })}
      />
      <MultiSelect
        label="Сегменты"
        options={(options?.segments ?? []).map((s) => ({ value: s, label: s }))}
        selected={value.segments ?? []}
        onChange={(v) => patch({ segments: v })}
      />
      <MultiSelect
        label="Города"
        options={(options?.cities ?? []).map((c) => ({ value: c, label: c }))}
        selected={value.cities ?? []}
        onChange={(v) => patch({ cities: v })}
      />
      <MultiSelect
        label="Бренды"
        options={(options?.brands ?? []).map((b) => ({ value: b, label: b }))}
        selected={value.brands ?? []}
        onChange={(v) => patch({ brands: v })}
        searchable
      />
      <select
        value={value.type ?? ''}
        onChange={(e) => patch({ type: e.target.value as SummaryFilterValues['type'] })}
        className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-gray-700 bg-white"
      >
        <option value="">Тип: все</option>
        <option value="TEKUCHKA">Текучка</option>
        <option value="PROJECT">Проект</option>
      </select>
      {active && (
        <button
          onClick={() => onChange({})}
          className="px-2.5 py-1.5 text-sm text-gray-500 hover:text-gray-800"
        >
          Сбросить
        </button>
      )}
    </div>
  )
}
